import { useQuery } from '@tanstack/react-query'
import { getRolloutFamily } from '@/api/priceRollout'
import { cn } from '@/lib/utils'
import { PORTAL_FARBE, portalName, rolloutStatistik, type PortalStatistik, type SichtPortal } from './rolloutView'

/**
 * Kopf-Statistik im Grundartikel (Plan 2026-10-30-rollout-ausnahme): je Portal mit aktiven Angeboten,
 * wie viele ausgerollt sind (am Ziel, Rollout läuft, Ist-Preis gehalten) und wie viele offen.
 * Liest dieselbe Familie wie der Reiter Preis-Rollout (gleicher Query-Schlüssel, kein zweiter Abruf).
 */
export function RolloutKopfStatistik({ sku, onOeffne }: {
  sku: string
  /** Öffnet den Reiter Preis-Rollout; mit Portal: nur offene Varianten dieses Portals. */
  onOeffne: (offenIn?: SichtPortal) => void
}) {
  const { data: fam } = useQuery({
    queryKey: ['rollout-family', sku],
    queryFn: () => getRolloutFamily(sku),
    staleTime: 15_000,
  })
  // Kopf darf nie an einer unerwarteten Antwort scheitern: dann einfach keine Statistik.
  if (!fam || !Array.isArray(fam.varianten)) return null
  const zeilen = rolloutStatistik(fam.varianten)
  if (zeilen.length === 0) return null
  const aktiv = zeilen.reduce((s, z) => s + z.aktiv, 0)
  const amZiel = zeilen.reduce((s, z) => s + z.am_ziel, 0)
  const offen = zeilen.reduce((s, z) => s + z.offen, 0)

  return (
    <section aria-label="Preis-Rollout aktiver Angebote" className="mt-3 max-w-md">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <button
          type="button" onClick={() => onOeffne()}
          className="text-[10px] font-semibold uppercase tracking-wider text-muted hover:text-primary focus-visible:outline-2 focus-visible:outline-primary sm:w-32 sm:text-left"
        >
          Preis-Rollout →
        </button>
        <span className="text-[11px] text-ink-2">
          {offen === 0
            ? <span className="font-semibold text-success">alle {aktiv} aktiven Angebote ausgerollt</span>
            : <><span className="font-mono font-semibold text-ink">{aktiv - offen}/{aktiv}</span> ausgerollt</>}
          {' · '}<span className="font-mono font-semibold text-ink">{amZiel}</span> am Ziel
        </span>
      </div>
      <ul className="mt-1.5 flex flex-col gap-1">
        {zeilen.map((z) => <PortalZeile key={z.portal} z={z} onOffen={() => onOeffne(z.portal)} />)}
      </ul>
    </section>
  )
}

const TEILE = [
  { feld: 'am_ziel', klasse: 'bg-success', text: 'am Ziel' },
  { feld: 'laeuft', klasse: 'bg-primary', text: 'Rollout läuft' },
  { feld: 'ausnahme', klasse: 'bg-warn', text: 'Ist-Preis gehalten' },
] as const

function PortalZeile({ z, onOffen }: { z: PortalStatistik; onOffen: () => void }) {
  const titel = [
    `${portalName(z.portal)}: ${z.aktiv} aktive Angebote`,
    ...TEILE.map((t) => `${z[t.feld]} ${t.text}`),
    `${z.offen} offen`,
  ].join(' · ')
  return (
    <li data-testid={`kopf-rollout-${z.portal}`} className="flex items-center gap-2 text-[11px]" title={titel}>
      <span className="inline-flex w-[4.5rem] shrink-0 items-center gap-1.5 text-ink-2">
        <span aria-hidden className="inline-block h-2.5 w-[3px] rounded-sm" style={{ background: PORTAL_FARBE[z.portal] }} />
        {portalName(z.portal)}
      </span>
      {/* Gestapelter Balken: grün am Ziel, blau läuft, gelb gehalten, Rest (offen) leer. */}
      <span aria-hidden className="flex h-1.5 min-w-16 flex-1 overflow-hidden rounded bg-panel-2 ring-1 ring-inset ring-line-2">
        {TEILE.map((t) => z[t.feld] > 0 && (
          <span key={t.feld} className={cn('h-full', t.klasse)} style={{ width: `${(z[t.feld] / z.aktiv) * 100}%` }} />
        ))}
      </span>
      <span className="w-12 shrink-0 text-right font-mono text-ink">{z.aktiv - z.offen}/{z.aktiv}</span>
      {z.offen > 0 ? (
        <button
          type="button" onClick={onOffen}
          className="w-16 shrink-0 text-left text-ink-2 hover:text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary"
        >
          {z.offen} offen
        </button>
      ) : (
        <span className="w-16 shrink-0 text-success">{z.am_ziel === z.aktiv ? 'am Ziel' : 'ausgerollt'}</span>
      )}
    </li>
  )
}
